
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatCurrency } from '@/lib/utils';
import { useCostAnalysis } from './hooks/useCostAnalysis';
import { DollarSign, TrendingUp, Package, BarChart3 } from 'lucide-react';

interface CostAnalysisModalProps {
  estimateId: string;
  isOpen: boolean;
  onClose: () => void;
}

const CostAnalysisModal: React.FC<CostAnalysisModalProps> = ({
  estimateId,
  isOpen,
  onClose,
}) => {
  const { data: costAnalysis, isLoading, error } = useCostAnalysis(estimateId);

  const getComponentTypeBadge = (type: string) => {
    switch (type) {
      case 'supplier_line_item':
        return <Badge variant="outline" className="text-blue-600 border-blue-200">Supplier</Badge>;
      case 'internal_line_item':
        return <Badge variant="outline" className="text-green-600 border-green-200">Internal</Badge>;
      case 'assembly':
        return <Badge variant="outline" className="text-purple-600 border-purple-200">Assembly</Badge>;
      case 'custom':
        return <Badge variant="outline" className="text-orange-600 border-orange-200">Custom</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <p>Loading cost analysis...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-destructive">Error loading cost analysis: {error.message}</p>
        </div>
      );
    }

    if (!costAnalysis) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">No cost analysis data available.</p>
        </div>
      );
    }

    return (
      <Tabs defaultValue="summary" className="w-full h-full flex flex-col">
        <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="categories">By Category</TabsTrigger>
          <TabsTrigger value="items">All Items</TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="space-y-6 flex-1 overflow-y-auto">
          {renderSummaryTab(costAnalysis, getComponentTypeBadge)}
        </TabsContent>

        <TabsContent value="categories" className="space-y-6 flex-1 overflow-y-auto">
          {renderCategoriesTab(costAnalysis, getComponentTypeBadge)}
        </TabsContent>

        <TabsContent value="items" className="space-y-6 flex-1 overflow-y-auto">
          {renderItemsTab(costAnalysis, getComponentTypeBadge)}
        </TabsContent>
      </Tabs>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Cost Analysis
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Extracted render functions to keep the component clean

function renderSummaryTab(costAnalysis: any, getComponentTypeBadge: (type: string) => React.ReactNode) {
  const hasDiscount = costAnalysis.discountPercentage > 0;
  const customerPrice = hasDiscount ? costAnalysis.totalFinalCostAfterDiscount : costAnalysis.totalFinalCost;
  const profitAmount = customerPrice - costAnalysis.totalBaseCost;
  const profitMargin = hasDiscount ? costAnalysis.overallMarginAfterDiscount : costAnalysis.overallMargin;
  const profitMarkup = hasDiscount ? costAnalysis.overallMarkupAfterDiscount : costAnalysis.overallMarkup;

  return (
    <>
      {/* Overall Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Base Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(costAnalysis.totalBaseCost)}
            </div>
            <p className="text-xs text-muted-foreground">Your actual costs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Customer Price</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(customerPrice)}
            </div>
            <p className="text-xs text-muted-foreground">
              {hasDiscount
                ? `After ${costAnalysis.discountPercentage.toFixed(2)}% discount (−${formatCurrency(costAnalysis.discountAmount)})`
                : 'Price to customer'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Amount</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${profitAmount >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {formatCurrency(profitAmount)}
            </div>
            <p className="text-xs text-muted-foreground">Your profit</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${profitMargin >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
              {profitMargin.toFixed(2)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {profitMarkup.toFixed(2)}% markup
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Discount Info Banner */}
      {hasDiscount && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-amber-700 border-amber-300 dark:text-amber-400 dark:border-amber-700">
                Discount Applied
              </Badge>
              <span className="text-sm text-muted-foreground">
                {costAnalysis.discountPercentage.toFixed(2)}% off — {formatCurrency(costAnalysis.discountAmount)} discount on {formatCurrency(costAnalysis.totalFinalCost)} subtotal
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Category Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Base Cost</TableHead>
                <TableHead className="text-right">Final Cost</TableHead>
                <TableHead className="text-right">Profit</TableHead>
                <TableHead className="text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {costAnalysis.categorySummaries.map((category: any) => (
                <TableRow key={category.categoryName}>
                  <TableCell className="font-medium">{category.categoryName}</TableCell>
                  <TableCell>{category.itemCount}</TableCell>
                  <TableCell className="text-right">{formatCurrency(category.totalBaseCost)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(category.totalFinalCost)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(category.totalFinalCost - category.totalBaseCost)}</TableCell>
                  <TableCell className="text-right">
                    <span className={category.averageMargin > 20 ? 'text-green-600' : category.averageMargin > 10 ? 'text-yellow-600' : 'text-red-600'}>
                      {category.averageMargin.toFixed(2)}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
function renderCategoriesTab(costAnalysis: any, getComponentTypeBadge: (type: string) => React.ReactNode) {
  return (
    <>
      {costAnalysis.categorySummaries.map((category: any) => {
        const categoryItems = costAnalysis.items.filter((item: any) => item.category === category.categoryName);
        return (
          <Card key={category.categoryName}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{category.categoryName}</span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">{category.itemCount} items</span>
                  <span className="font-medium">
                    Profit: {formatCurrency(category.totalFinalCost - category.totalBaseCost)} ({category.averageMargin.toFixed(2)}%)
                  </span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="overflow-x-auto">
                  <Table className="min-w-[800px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Description</TableHead>
                        <TableHead className="min-w-[100px]">Type</TableHead>
                        <TableHead className="text-right min-w-[80px]">Qty</TableHead>
                        <TableHead className="text-right min-w-[120px]">Unit Base Cost</TableHead>
                        <TableHead className="text-right min-w-[120px]">Unit Final Cost</TableHead>
                        <TableHead className="text-right min-w-[120px]">Total Base Cost</TableHead>
                        <TableHead className="text-right min-w-[120px]">Total Final Cost</TableHead>
                        <TableHead className="text-right min-w-[80px]">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryItems.map((item: any) => {
                        const unitBaseCost = item.quantity > 0 ? item.baseCost / item.quantity : 0;
                        const unitFinalCost = item.quantity > 0 ? item.finalCost / item.quantity : 0;
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.description}</TableCell>
                            <TableCell>{getComponentTypeBadge(item.componentType)}</TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(unitBaseCost)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(unitFinalCost)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.baseCost)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.finalCost)}</TableCell>
                            <TableCell className="text-right">
                              <span className={item.margin > 20 ? 'text-green-600' : item.margin > 10 ? 'text-yellow-600' : 'text-red-600'}>
                                {item.margin.toFixed(2)}%
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        );
      })}
    </>
  );
}

function renderItemsTab(costAnalysis: any, getComponentTypeBadge: (type: string) => React.ReactNode) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>All Line Items</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[500px]">
          <div className="overflow-x-auto">
            <Table className="min-w-[1000px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Description</TableHead>
                  <TableHead className="min-w-[100px]">Category</TableHead>
                  <TableHead className="min-w-[100px]">Type</TableHead>
                  <TableHead className="text-right min-w-[80px]">Qty</TableHead>
                  <TableHead className="text-right min-w-[120px]">Unit Base Cost</TableHead>
                  <TableHead className="text-right min-w-[120px]">Unit Final Cost</TableHead>
                  <TableHead className="text-right min-w-[120px]">Total Base Cost</TableHead>
                  <TableHead className="text-right min-w-[120px]">Total Final Cost</TableHead>
                  <TableHead className="text-right min-w-[100px]">Profit</TableHead>
                  <TableHead className="text-right min-w-[80px]">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costAnalysis.items.map((item: any) => {
                  const unitBaseCost = item.quantity > 0 ? item.baseCost / item.quantity : 0;
                  const unitFinalCost = item.quantity > 0 ? item.finalCost / item.quantity : 0;
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.description}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{getComponentTypeBadge(item.componentType)}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(unitBaseCost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(unitFinalCost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.baseCost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.finalCost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.finalCost - item.baseCost)}</TableCell>
                      <TableCell className="text-right">
                        <span className={item.margin > 20 ? 'text-green-600' : item.margin > 10 ? 'text-yellow-600' : 'text-red-600'}>
                          {item.margin.toFixed(2)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

export default CostAnalysisModal;
